import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { expenseAPI, vehicleAPI } from '../api/client';
import { Wallet, Plus, TrendingUp, Calendar, Receipt, Upload, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, byCategory: [] });
  const [uploadingFor, setUploadingFor] = useState(null); // expense_id currently uploading
  const receiptInputRef = useRef(null);
  const receiptTargetRef = useRef(null); // expense_id the hidden input applies to
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, vehiclesRes] = await Promise.all([
        expenseAPI.list(),
        vehicleAPI.list()
      ]);
      setExpenses(expensesRes.data);
      setVehicles(vehiclesRes.data);
      calculateStats(expensesRes.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (expenseList) => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    const total = expenseList.reduce((sum, exp) => sum + exp.amount, 0);
    const thisMonth = expenseList
      .filter(exp => {
        const date = parseISO(exp.date);
        return date >= monthStart && date <= monthEnd;
      })
      .reduce((sum, exp) => sum + exp.amount, 0);

    const categoryTotals = expenseList.reduce((acc, exp) => {
      acc[exp.category] = (acc[exp.category] || 0) + exp.amount;
      return acc;
    }, {});

    const byCategory = Object.entries(categoryTotals).map(([name, value]) => ({
      name,
      value
    }));

    setStats({ total, thisMonth, byCategory });
  };

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  const triggerReceiptUpload = (expenseId) => {
    receiptTargetRef.current = expenseId;
    receiptInputRef.current?.click();
  };

  const handleReceiptFileChange = async (event) => {
    const expenseId = receiptTargetRef.current;
    const file = event.target.files?.[0];
    if (!file || !expenseId) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Receipt must be under 10MB');
      return;
    }
    setUploadingFor(expenseId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${backendUrl}/api/expenses/${expenseId}/receipt`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!resp.ok) throw new Error('Upload failed');
      const data = await resp.json();
      toast.success('Receipt attached');
      // Update the in-memory list so the row shows the new receipt_url immediately
      setExpenses((list) =>
        list.map((e) => (e.expense_id === expenseId ? { ...e, receipt_url: data.receipt_url } : e))
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingFor(null);
      receiptTargetRef.current = null;
      if (receiptInputRef.current) receiptInputRef.current.value = '';
    }
  };

  const viewReceipt = async (expenseId) => {
    try {
      const resp = await fetch(`${backendUrl}/api/expenses/${expenseId}/receipt-url`, {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Could not generate receipt URL');
      const data = await resp.json();
      const url = data.url?.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      toast.error('Failed to open receipt');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 pb-6"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Expenses</h1>
          <p className="text-muted-foreground mt-1">Track your vehicle costs and spending</p>
        </div>
        <Button asChild size="lg" className="rounded-xl" data-testid="add-expense-button">
          <Link to="/app/expenses/add">
            <Plus className="h-5 w-5 mr-2" />
            Add Expense
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-3"
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Expenses</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-teal))]/10 text-[hsl(var(--brand-teal))] flex items-center justify-center">
                <Wallet className="h-5 w-5" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold counter-number" data-testid="total-expenses">
              £{stats.total.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">This Month</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-sky))]/10 text-[hsl(var(--brand-sky))] flex items-center justify-center">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold counter-number" data-testid="monthly-expenses">
              £{stats.thisMonth.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Transactions</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-coral))]/10 text-[hsl(var(--brand-coral))] flex items-center justify-center">
                <Receipt className="h-5 w-5" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold counter-number" data-testid="transaction-count">
              {expenses.length}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Charts */}
      {stats.byCategory.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid gap-4 md:grid-cols-2"
        >
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Spending by Category</CardTitle>
              <CardDescription>Total expenses breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.byCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => entry.name}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.byCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `£${value.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Category Breakdown</CardTitle>
              <CardDescription>Expense amounts by category</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.byCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip formatter={(value) => `£${value.toFixed(2)}`} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Recent Expenses */}
      {expenses.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-2xl font-semibold mb-4">Recent Expenses</h2>
          {/* Hidden global file input; re-used per row via triggerReceiptUpload */}
          <input
            ref={receiptInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleReceiptFileChange}
            className="hidden"
            data-testid="expense-receipt-input"
          />
          <div className="space-y-3" data-testid="expenses-list">
            {expenses.slice(0, 10).map((expense, index) => (
              <motion.div
                key={expense.expense_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.05 }}
              >
                <Card className="card-hover">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Wallet className="h-6 w-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{expense.category}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(parseISO(expense.date), 'dd MMM yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xl font-semibold counter-number">£{expense.amount.toFixed(2)}</p>
                        </div>
                        {expense.receipt_url ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewReceipt(expense.expense_id)}
                            data-testid={`expense-receipt-view-${expense.expense_id}`}
                            title="View receipt"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" /> Receipt
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => triggerReceiptUpload(expense.expense_id)}
                            disabled={uploadingFor === expense.expense_id}
                            data-testid={`expense-receipt-upload-${expense.expense_id}`}
                            title="Attach receipt"
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            {uploadingFor === expense.expense_id ? 'Uploading…' : 'Attach'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ) : (
        <Card className="card-elevated">
          <CardContent className="py-16 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Wallet className="h-10 w-10 text-primary" />
            </div>
            <h3 className="font-semibold text-2xl mb-2">No expenses yet</h3>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Start tracking your vehicle expenses to monitor spending and identify savings opportunities.
            </p>
            <Button asChild size="lg" className="rounded-xl">
              <Link to="/app/expenses/add">
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Expense
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};

export default Expenses;
